import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isBetween from "dayjs/plugin/isBetween";
import { useSelector } from "react-redux";
import ChevronIcon from "../../../../../Common/Svgs/ChevronIcon";
import CustomInput from "../../../../../Core/Components/CustomInput";
import CloseIcon from "../../../../../Common/Svgs/CloseIcon";
import TimeOffRequests from "./TimeOffRequests";
import FilterItem from "../../../../../Core/Components/FilterItem";
import { LOCATION_TYPE } from "../../../../Location/Data";

dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);

const Header = ({
  search,
  setSearch,
  filters,
  setFilters,
  setHasMore,
  setPage,
}) => {
  const { allLocations } = useSelector((state) => state.locations);
  const { userDetails } = useSelector((state) => state.loggedInUser);

  const [focus, setFocus] = useState(false);

  const hubs = useMemo(() => {
    return allLocations
      .filter(
        (item) =>
          item.type === LOCATION_TYPE.HUB_STATION &&
          userDetails.hubs.includes(item.id),
      )
      .map((location) => ({
        id: location.id,
        value: location.name,
      }));
  }, [allLocations, userDetails.hubs]);

  const onSelect = (key, value) => {
    if (filters[key].includes(value)) {
      const newFilters = filters[key].filter((item) => item !== value);
      setFilters({ ...filters, [key]: newFilters });
    } else {
      setFilters({ ...filters, [key]: [...filters[key], value] });
    }

    setHasMore(true);
    setPage(1);
  };

  const isToday = dayjs().isBetween(
    dayjs(filters?.startDate),
    dayjs(filters?.endDate),
    null,
    "[]",
  );

  const onDatePrev = () => {
    const start = dayjs(filters?.startDate)
      .subtract("7", "days")
      .startOf("week");

    const end = dayjs(filters?.endDate).subtract("7", "days").endOf("week");

    setFilters((prev) => ({ ...prev, startDate: start, endDate: end }));
    setHasMore(true);
    setPage(1);
  };

  const onDateNext = () => {
    const start = dayjs(filters?.startDate).add("7", "days").startOf("week");
    const end = dayjs(filters?.endDate).add("7", "days").endOf("week");

    setFilters((prev) => ({ ...prev, startDate: start, endDate: end }));
    setHasMore(true);
    setPage(1);
  };

  const onToday = () => {
    const start = dayjs().startOf("week");
    const end = dayjs().endOf("week");

    setFilters((prev) => ({ ...prev, startDate: start, endDate: end }));
    setHasMore(true);
    setPage(1);
  };

  return (
    <div className="bg-[#FCFCFC] py-[9px] px-6 border-b border-light-grey flex items-center justify-between">
      <div className="flex items-center gap-x-3">
        <button
          type="button"
          className={`rounded-md h-[42px] py-3 px-4 ${
            isToday
              ? "bg-[#DFDFDF] text-[#AAAAAA] cursor-not-allowed"
              : "bg-green-10 text-green cursor-pointer"
          }`}
          disabled={isToday}
          onClick={onToday}
        >
          <p className="font-poppins font-medium text-xs ">Today</p>
        </button>

        <div role="button" onClick={onDatePrev}>
          <ChevronIcon className="w-4 h-4 stroke-black rotate-180" />
        </div>

        <div className="bg-background flex items-center justify-center px-4 rounded-md h-[42px]">
          <p className="font-poppins font-medium text-base leading-[100%] text-black">
            {dayjs(filters?.startDate).format("MMM DD")} -{" "}
            {dayjs(filters?.endDate).format("MMM DD")}
          </p>
        </div>

        <div role="button" onClick={onDateNext}>
          <ChevronIcon className="w-4 h-4 stroke-black" />
        </div>
      </div>

      <div className="flex items-center gap-x-3">
        <CustomInput
          intent="search"
          prefix={
            <img src="/assets/icons/search.svg" alt="" className="mr-2" />
          }
          value={search}
          focus={focus}
          border={focus ? "success" : "none"}
          placeholder={focus ? null : "Search Drivers"}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          suffix={
            search.length > 0 ? (
              <CloseIcon
                className="w-4 h-4 cursor-pointer stroke-dark-grey-text"
                onClick={() => {
                  setFocus(false);
                  setSearch("");
                }}
              />
            ) : null
          }
        />

        <FilterItem
          label="Hub Station"
          identifier="hub"
          contentData={hubs}
          onSelect={onSelect}
          selectedValues={filters.hub}
          className="max-h-[200px] overflow-y-scroll"
          btnClassName="bg-[#E6EDEE] text-[#085054] hover:bg-primary-20"
          enableSearch
        />

        <TimeOffRequests />
      </div>
    </div>
  );
};

export default Header;
